import { Alert, Box, Button, Chip, Stack, Typography, useMediaQuery } from '@mui/material';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { TableDisplay } from '../../miscellaneous-components/table-display';
import { TableActionButtons } from '../../miscellaneous-components/table-action-buttons';
import { Add, ContentCopyOutlined, Visibility } from '@mui/icons-material';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import gaiaTheme from '../../../../root-components/theme';
import AssumptionService from '../../../../shared/services/assumption/assumption.service';
import { IPostAssumption, IRegisterEntry, RegisterSource } from '../../../../shared/services/assumption/assumption.model';
import { AddAssumptionDialog } from '../../../dialogs/add-assumption-dialog';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { LoadingPage } from '../../../pages/loading-page';
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { CloneAssumptionDialog } from '../../../dialogs/clone-assumption-dialog';
import { userHasRequiredRole } from '../../../../shared/utils/role-utils';

const SOURCE_LABELS: Record<RegisterSource, string> = {
  assumption: 'Assumption',
  acknowledgedFinding: 'Readiness finding',
}

// The notes panel's muted chip (notes-panel.tsx), so a source reads as a label, not a status.
const mutedChipSx = { color: 'text.secondary', borderColor: 'lightGrey.main', flexShrink: 0 }

// Status as the app's outlined status chips do it (action-status-chip.tsx, readiness-checks-list.tsx):
// an acknowledged finding is green, as on the Report Readiness page; "Uncertain" is amber because it
// is the assumption an auditor will probe; every other certainty level (and any legacy value such as
// "Likely") is a neutral ink outline.
const RegisterStatusChip: FunctionComponent<{ status: string }> = ({ status }) => {
  if (status === 'Acknowledged') return <Chip size='small' variant='outlined' color='success' label={status} />
  if (status === 'Uncertain') return <Chip size='small' variant='outlined' color='warning' label={status} />
  return <Chip size='small' variant='outlined' label={status} sx={{ borderColor: 'text.primary', color: 'text.primary' }} />
}

const rowIdOf = (entry: IRegisterEntry) => `${entry.source}:${entry.sourceId}`

/**
 * The assumptions register for the selected period, exactly as the SECR annex, the UK SRS report
 * and the audit pack print it: recorded assumptions, then acknowledged readiness findings.
 * Assumptions are added and edited here; an acknowledged finding opens Report Readiness, where it is
 * changed. Notes are never in this list: they explain records and reach the audit pack only. On a
 * group page, that group's assumptions and the acknowledged findings about that group.
 */
export const EmissionsAssumptionsTable: FunctionComponent<{
  isGroupPage?: boolean,
  groupId?: string,
}> = ({ isGroupPage = false, groupId }) => {
  //Services
  const assumptionService = useMemo(() => new AssumptionService(), []);
  const subsidiaryService = SubsidiaryService.getInstance();
  const navigate = useNavigate();
  //Context
  const userDetailsContext = useContext(UserDetailsContext)
  const organisation = userDetailsContext.userDetails?.organisation
  const canEdit = userHasRequiredRole(userDetailsContext.userDetails, ['admin', 'analyst'])
  const userNotificationsContext = useContext(UserNotificationsContext)
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const reportPeriodContext = useContext(ReportPeriodContext)
  const reportPeriodId = reportPeriodContext.lastSelectedReportId
  const hasClonablePeriods = reportPeriodContext.openReportPeriods.some(period => period.id !== reportPeriodId)
  //State
  const [errorCode, setErrorCode] = useState<number>()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [entries, setEntries] = useState<IRegisterEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loadError, setLoadError] = useState('')
  const [assumptionId, setAssumptionId] = useState<string>()
  const assumption: IPostAssumption = {
    name: '',
    description: '',
    status: '',
    groupId: groupId ? groupId : '',
    subsidiaries: [],
    additionalInformation: '',
  }
  const [openDialog, setDialogOpen] = React.useState(false);
  const [cloneAssumptionId, setCloneAssumptionId] = useState<string>();
  const [isCloning, setIsCloning] = useState<boolean>(false);
  const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [change, setChange] = useState<number>(0)
  //Loading State
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDialogSubmitting, setIsDialogSubmititng] = useState<boolean>(false)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  //Use Effects
  useEffect(() => {
    subsidiaryService.getAll()
      .then(response => (
        setSubsidiaries(response)
      ))
      .catch(err => {
        console.log(err)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!reportPeriodId) {
      return
    }
    let isActive = true
    setLoadError('')
    assumptionService.getRegister(reportPeriodId, groupId)
      .then(response => {
        if (!isActive) return
        setEntries(response.entries)
        setTotal(response.total)
      })
      .catch(err => {
        console.log(err)
        if (isActive) setLoadError('Unable to load the assumptions register. Please try again.')
      })
      .finally(() => {
        if (isActive) setIsLoading(false)
      })
    return () => {
      isActive = false
    }
  }, [assumptionService, reportPeriodId, groupId, change])

  const refreshRegister = () => setChange(current => current + 1)

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'name', numeric: false, label: 'Name', sortable: true, isHeading: true },
    { id: 'source', numeric: false, label: 'Source', sortable: true, sortValue: 'sourceLabel' },
    ...(!isGroupPage ? [{ id: 'group', numeric: false, label: 'Group', sortable: true }] : []),
    ...(organisation && organisation.enableSubsidiaries ? [{ id: 'subsidiaries', numeric: false, label: screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' }), sortable: false }] : []),
    { id: 'description', numeric: false, label: 'Description', sortable: true, sortValue: 'descriptionText' },
    { id: 'status', numeric: false, label: 'Status', sortable: true, isSubHeading: true, sortValue: 'statusText' },
  ];

  const rows = entries.map((entry, index) => ({
    id: rowIdOf(entry),
    // Report order is the default sort: the table starts in the order the reports print.
    reportOrder: index,
    name: entry.name,
    source: <Chip size='small' variant='outlined' label={SOURCE_LABELS[entry.source]} sx={mutedChipSx} />,
    sourceLabel: SOURCE_LABELS[entry.source],
    group: entry.groupName ?? '',
    subsidiaries: entry.subsidiaries.map(subsidiary => subsidiaries?.find(s => s.id === subsidiary)?.name).filter(Boolean).join(', '),
    // Additional Information beneath the description, as the UK SRS report shows it.
    description: (
      <Box component='span' sx={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {entry.description}
        {entry.additionalInformation && (
          <Typography variant='body2' component='span' sx={{ display: 'block', color: 'text.secondary' }}>
            {entry.additionalInformation}
          </Typography>
        )}
      </Box>
    ),
    descriptionText: entry.description,
    status: entry.status ? <RegisterStatusChip status={entry.status} /> : '',
    statusText: entry.status,
    // Every row has the same menu so the Action column is never empty. A recorded assumption is
    // edited here; an acknowledged finding opens Report Readiness (the same place a click on the row
    // goes), with the menu item saying where.
    action: entry.source === 'assumption'
      ? (canEdit ? (
        <TableActionButtons
          id={entry.sourceId}
          includeEdit={true}
          includeDelete={true}
          onEdit={() => editClickHandler(entry.sourceId)}
          onDelete={() => deleteClickHandler(entry.sourceId)}
          additionalMenuItems={[{
            label: 'Clone to report period',
            onClick: () => cloneClickHandler(entry.sourceId),
            disabled: !hasClonablePeriods,
            icon: <ContentCopyOutlined sx={{ cursor: 'pointer', mr: 1 }} />,
          }]}
        />
      ) : null)
      : (
        <TableActionButtons
          id={rowIdOf(entry)}
          additionalMenuItems={[{
            label: 'Open in Report Readiness',
            onClick: () => openAcknowledgedFinding(),
            icon: <Visibility sx={{ cursor: 'pointer', mr: 1 }} />,
          }]}
        />
      ),
  }))

  // An acknowledged finding is changed in Report Readiness: open it on the acknowledged findings.
  const openAcknowledgedFinding = () => {
    navigate('/readiness?tabId=0&view=acknowledged')
  }

  const rowClickHandler = (rowId: string) => {
    const entry = entries.find(e => rowIdOf(e) === rowId)
    if (!entry) return
    if (entry.source === 'assumption') {
      if (canEdit) editClickHandler(entry.sourceId)
      return
    }
    openAcknowledgedFinding()
  }

  const resetDialogErrors = () => {
    setErrorCode(undefined)
    setErrorMessage(undefined)
  }

  const editClickHandler = (id: string) => {
    resetDialogErrors()
    setAssumptionId(id)
    setDialogOpen(true);
  }

  const deleteClickHandler = (id: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `Are you sure you would like to delete this assumption? This action cannot be undone.`,
      buttonCopy: 'Continue',
      hideCancelButton: false
    })
      .then(response => {
        if (!response) return
        assumptionService.remove(id)
          .then(() => {
            refreshRegister()
            setAssumptionId('')
            userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
          })
          .catch(err => {
            console.log(err)
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          })
      })
  }

  const cloneClickHandler = (id: string) => {
    setCloneAssumptionId(id)
  }

  const cloneCancelHandler = () => {
    setCloneAssumptionId(undefined)
  }

  const cloneHandler = (reportPeriodIds: string[]) => {
    if (!cloneAssumptionId || isCloning) {
      return
    }
    setIsCloning(true)
    assumptionService.clone(cloneAssumptionId, reportPeriodIds)
      .then(response => {
        setCloneAssumptionId(undefined)
        setIsCloning(false)
        userNotificationsContext.openSnackbar({ open: true, message: response.message ?? 'Cloned successfully' });
      })
      .catch(err => {
        setIsCloning(false)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
        console.log(err)
      })
  }

  const cancelHandler = () => {
    setDialogOpen(false);
    setAssumptionId('')
    resetDialogErrors()
  }

  const showServerError = (err: any) => {
    if (err?.response?.data?.message) {
      setErrorMessage(err.response.data.message)
      setErrorCode(err.response.data.code)
    } else {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
    }
  }

  const createHandler = (values: IPostAssumption, resetForm: () => void, setSelectedSubsidiariesId: React.Dispatch<React.SetStateAction<any>>,) => {
    if (!isDialogSubmitting) {
      setIsDialogSubmititng(true)
      assumptionService.create({ ...values, reportPeriodId })
        .then(() => {
          setDialogOpen(false);
          resetForm();
          setSelectedSubsidiariesId([]);
          refreshRegister()
          setAssumptionId('')
          resetDialogErrors()
          setIsDialogSubmititng(false)
          userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
        })
        .catch(err => {
          showServerError(err)
          setIsDialogSubmititng(false)
          console.log(err)
        })
    } else {
      userNotificationsContext.openSnackbar({ open: true, message: 'Saving in progress, please wait' });
    }
  }

  const editHandler = (values: IPostAssumption) => {
    if (assumptionId) {
      if (!isDialogSubmitting) {
        setIsDialogSubmititng(true)
        assumptionService.update(assumptionId, values)
          .then(() => {
            setDialogOpen(false);
            refreshRegister()
            setAssumptionId('')
            resetDialogErrors()
            setIsDialogSubmititng(false)
            userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
          })
          .catch(err => {
            showServerError(err)
            setIsDialogSubmititng(false)
            console.log(err)
          })
      } else {
        userNotificationsContext.openSnackbar({ open: true, message: 'Saving in progress, please wait' });
      }
    } else {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
    }
  }

  const addAssumptionHandler = () => {
    resetDialogErrors()
    setAssumptionId('')
    setDialogOpen(true)
  }

  const explanation = isGroupPage
    ? "This group's assumptions, and readiness findings about this group that you have acknowledged. Everything listed here is printed in your SECR and UK SRS reports and in the audit pack."
    : 'Everything listed here is printed in your SECR and UK SRS reports and in the audit pack. Add assumptions here. Readiness findings you have acknowledged are listed too; change those in Report Readiness.'

  return (
    <>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent='space-between' gap={1} sx={{ mb: 2 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
          {explanation}
        </Typography>
        {canEdit &&
          <Button variant='text' onClick={addAssumptionHandler} sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', md: 'flex-start' } }}>
            <Add sx={{ color: 'primary.main' }} />
            <Typography>Add Assumption</Typography>
          </Button>}
      </Stack>
      <LoadingPage pageReady={!isLoading}>
        {loadError && <Alert severity='error'>{loadError}</Alert>}
        {!loadError && entries.length === 0 &&
          <Typography variant='body2' sx={{ color: 'text.secondary', py: 1 }}>
            {isGroupPage ? 'No assumptions or acknowledged readiness findings for this group yet.' : 'No assumptions or acknowledged readiness findings for this period yet.'}
          </Typography>}
        {!loadError && entries.length > 0 &&
          <>
            {total > entries.length &&
              <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
                Showing the first {entries.length.toLocaleString('en-GB')} of {total.toLocaleString('en-GB')} entries.
              </Typography>}
            <TableDisplay
              isMobileMode={isMobileMode}
              onRowClick={rowClickHandler}
              headCells={headCells}
              rows={rows}
              defaultOrderBy='reportOrder'
              selected={selected}
              setSelected={setSelected} />
          </>}
      </LoadingPage>
      {assumptionId && openDialog &&
        <AddAssumptionDialog
          isSubmitting={isDialogSubmitting}
          assumption={assumption}
          assumptionId={assumptionId}
          open={openDialog}
          errorCode={errorCode}
          errorMessage={errorMessage}
          successHandler={editHandler}
          cancelHandler={cancelHandler} />}
      {!assumptionId && openDialog &&
        <AddAssumptionDialog
          isSubmitting={isDialogSubmitting}
          assumption={assumption}
          open={openDialog}
          errorCode={errorCode}
          errorMessage={errorMessage}
          successHandler={createHandler}
          cancelHandler={cancelHandler} />}
      {cloneAssumptionId &&
        <CloneAssumptionDialog
          open={Boolean(cloneAssumptionId)}
          isSubmitting={isCloning}
          successHandler={cloneHandler}
          cancelHandler={cloneCancelHandler} />}
    </>
  )
}
